import { isBefore, parseISO } from 'date-fns';
import { z } from 'zod';

import { toDateInputValue } from './point-expiry-form-helpers';

type TranslateError = (key: string) => string;

const toDay = (value: string): Date => parseISO(toDateInputValue(value));

// A period has a required end and warning date, plus an optional start. Ordering
// rules: warning strictly before end; when a start is present, end on or after
// start and warning strictly after start. When start is absent the period begins
// immediately, so start-relative rules are skipped.
export const createPointExpiryFormSchema = (t: TranslateError) =>
	z
		.object({
			startDate: z.string().trim().optional().default(''),
			endDate: z.string().trim().min(1, t('errorEndDateRequired')),
			warningDate: z.string().trim().min(1, t('errorWarningDateRequired')),
		})
		.superRefine((data, ctx) => {
			if (!data.endDate || !data.warningDate) {
				return;
			}

			const end = toDay(data.endDate);
			const warning = toDay(data.warningDate);

			// Warning must land strictly before the expiry so admins have lead time.
			if (!isBefore(warning, end)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['warningDate'],
					message: t('errorWarningBeforeEnd'),
				});
			}

			if (!data.startDate) {
				return;
			}

			const start = toDay(data.startDate);

			// End must be on or after the start.
			if (isBefore(end, start)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['endDate'],
					message: t('errorEndAfterStart'),
				});
			}

			// Warning must land strictly after the start.
			if (!isBefore(start, warning)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					path: ['warningDate'],
					message: t('errorWarningAfterStart'),
				});
			}
		});

export type PointExpiryFormData = z.infer<ReturnType<typeof createPointExpiryFormSchema>>;

export const DEFAULT_POINT_EXPIRY_FORM_VALUES: PointExpiryFormData = {
	startDate: '',
	endDate: '',
	warningDate: '',
};
